import { Trans } from "@lingui/react/macro"
import { useQuery } from "@tanstack/react-query"
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"
import { LomPortalContext } from "@/features/list-of-materials/components/form/LomPortal"
import { LomSideNav } from "@/features/list-of-materials/components/layout/LomSideNav"
import { VersionBanner } from "@/features/list-of-materials/components/layout/VersionBanner"
import { VersionSelect } from "@/features/list-of-materials/components/layout/VersionSelect"
import { Block, EmptyLine } from "@/features/list-of-materials/components/ui/Block"
import { TextLink } from "@/features/list-of-materials/components/ui/Button"
import {
  lomOverviewQueryOptions,
  lomVersionsQueryOptions,
  useLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials")({
  validateSearch: z.object({ version: z.string().optional() }),
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (selected) void context.queryClient.prefetchQuery(lomOverviewQueryOptions(selected.id))
  },
  head: () => ({
    links: [
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap",
      },
    ],
  }),
  component: ListOfMaterialsLayout,
})

function ListOfMaterialsLayout() {
  const navigate = useNavigate()
  const { versions, active, selected, search } = useLomVersion()
  const { data: overview } = useQuery({
    ...lomOverviewQueryOptions(selected?.id ?? ""),
    enabled: !!selected,
  })

  const [root, setRoot] = useState<HTMLDivElement | null>(null)

  const selectVersion = (id: string) =>
    void navigate({ to: ".", search: id === active?.id ? {} : { version: id } })

  return (
    <LomPortalContext.Provider value={root}>
      <div
        ref={setRoot}
        className="font-lom text-lom-ink bg-lom-canvas flex h-full min-h-0 text-base antialiased tabular-nums [color-scheme:light] max-[900px]:flex-col"
      >
        <LomSideNav
          overview={overview}
          version={search.version}
          footer={
            <VersionSelect versions={versions} selectedId={selected?.id} onChange={selectVersion} />
          }
        />
        <main className="min-w-0 flex-1 overflow-y-auto px-8 pt-8 pb-12 max-[900px]:p-4">
          <div className="max-w-[1280px]">
            {selected && active && selected.id !== active.id && (
              <VersionBanner
                selected={selected}
                active={active}
                onBackToActive={() => selectVersion(active.id)}
              />
            )}
            {selected ? (
              <Outlet />
            ) : (
              <Block>
                <EmptyLine>
                  <Trans>No configuration version is active.</Trans>{" "}
                  <TextLink to="/settings/configuration-versions">
                    <Trans>Activate one under Settings</Trans>
                  </TextLink>
                </EmptyLine>
              </Block>
            )}
          </div>
        </main>
      </div>
    </LomPortalContext.Provider>
  )
}
